import type { ComponentType, ReactNode } from 'react';

type ConditionalWrapperParams = {
    condition: boolean;
    wrapper:
        | ComponentType<{ children: ReactNode; [key: string]: any }>
        | ComponentType<{ children?: ReactNode; [key: string]: any }>;
    wrapperProps?: Record<string, unknown>;
    children: ReactNode;
};

/**
 * A component that conditionally wraps its children with a specified wrapper component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.condition - Determines whether to apply the wrapper or not.
 * @param {React.ComponentType} props.wrapper - The wrapper component to use if the condition is true.
 * @param {Object}[props.wrapperProps] - Props to pass to the wrapper component.
 * @param {React.ReactNode} props.children - The content to be wrapped.
 *
 * @returns {React.ReactNode} The wrapped children if the condition is true, otherwise the children as-is.
 *
 * @example
 * // Use with custom wrapper props
 * <ConditionalWrapper
 *   condition={hasError}
 *   wrapper={ErrorBoundary}
 *   wrapperProps={{ fallback: <ErrorMessage /> }}
 * >
 *   <MyComponent />
 * </ConditionalWrapper>
 */
export const ConditionalWrapper = ({
    condition,
    wrapper: Wrapper,
    wrapperProps,
    children,
}: ConditionalWrapperParams): ReactNode =>
    condition ? <Wrapper {...wrapperProps}>{children}</Wrapper> : children;
